import { useState, useEffect } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StatusBadge from '../../components/shared/StatusBadge';
import { StarIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

export default function TalentPoolPage() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadPool(); }, []);

  const loadPool = async () => {
    try {
      const { data } = await api.get('/recruitment/talent-pool');
      setCandidates(data);
    } catch (err) {
      toast.error('Failed to load talent pool');
    } finally {
      setLoading(false);
    }
  };

  const reEngage = async (id) => {
    try {
      await api.patch(`/recruitment/candidates/${id}`, { stage: 'interview_scheduled' });
      toast.success('Candidate re-engaged');
      loadPool();
    } catch (err) {
      toast.error('Failed to re-engage');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  return (
    <div className="animate-slide-up">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Talent Pool</h1>
      <p className="text-gray-500 text-sm mb-6">{candidates.length} candidate{candidates.length !== 1 ? 's' : ''} on hold</p>

      {candidates.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <p className="text-gray-500">No candidates in the talent pool</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {candidates.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-gray-900">{c.first_name} {c.last_name}</p>
                  <p className="text-xs text-gray-500">{c.email}</p>
                </div>
                {c.interview_score && (
                  <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-lg">
                    <StarIcon className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-bold text-amber-700">{c.interview_score}/5</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 mb-3">
                <StatusBadge status={c.role_applied} />
                {c.fast_track && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Fast Track</span>}
              </div>
              {c.interview_notes && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{c.interview_notes}</p>
              )}
              <button
                onClick={() => reEngage(c.id)}
                className="w-full flex items-center justify-center gap-2 text-sm font-medium text-orange-500 border border-orange-200 rounded-xl py-2 hover:bg-orange-50 transition-colors"
              >
                <ArrowPathIcon className="w-4 h-4" /> Re-engage
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
